import { useState } from "react";
import { format, formatDistanceToNow } from "date-fns";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, MapPin, AlertCircle } from "lucide-react";
import type { OrgAwaitingSubmissionShift } from "../hooks/useAwaitingSubmissionShifts";
import type { GroupedProvider } from "../utils/group-time-entries";
import { calculateHours, formatHours } from "../utils/group-time-entries";

interface AwaitingSubmissionTableProps {
  groupedShifts: GroupedProvider<OrgAwaitingSubmissionShift>[];
}

export function AwaitingSubmissionTable({
  groupedShifts,
}: AwaitingSubmissionTableProps) {
  const [expandedProviders, setExpandedProviders] = useState<Set<string>>(
    new Set(groupedShifts.map((g) => g.providerId)),
  );

  const toggleProvider = (providerId: string) => {
    const newExpanded = new Set(expandedProviders);
    if (newExpanded.has(providerId)) {
      newExpanded.delete(providerId);
    } else {
      newExpanded.add(providerId);
    }
    setExpandedProviders(newExpanded);
  };

  return (
    <div className="space-y-4">
      {/* Provider groups */}
      {groupedShifts.map((provider) => {
        const isExpanded = expandedProviders.has(provider.providerId);

        return (
          <Card
            key={provider.providerId}
            className="overflow-hidden border-slate-200"
          >
            <Collapsible open={isExpanded}>
              <CollapsibleTrigger asChild>
                <div
                  className="flex items-center justify-between px-4 py-3 bg-amber-50 hover:bg-amber-100 transition-colors cursor-pointer"
                  onClick={() => toggleProvider(provider.providerId)}
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-amber-600" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-amber-600" />
                    )}
                    <span className="font-semibold text-slate-900">
                      {provider.providerName}
                    </span>
                    <Badge
                      variant="outline"
                      className="text-xs bg-amber-100 text-amber-700 border-amber-300"
                    >
                      {provider.totalEntries}{" "}
                      {provider.totalEntries === 1
                        ? "shift awaiting"
                        : "shifts awaiting"}
                    </Badge>
                  </div>
                </div>
              </CollapsibleTrigger>

              <CollapsibleContent>
                {provider.facilities.map((facility) => {
                  return (
                    <div key={facility.facilityId} className="border-t">
                      {/* Facility header */}
                      <div className="bg-slate-50 px-4 py-2.5 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <MapPin className="h-4 w-4 text-slate-400" />
                          <div>
                            <div className="font-medium text-slate-800">
                              {facility.facilityName}
                            </div>
                            {facility.facilityLocation && (
                              <div className="text-xs text-slate-500">
                                {facility.facilityLocation}
                              </div>
                            )}
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {facility.entries.length}{" "}
                          {facility.entries.length === 1 ? "shift" : "shifts"}
                        </Badge>
                      </div>

                      {/* Desktop table */}
                      <div className="hidden sm:block">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-white text-xs">
                              <TableHead>Date</TableHead>
                              <TableHead>Scheduled Time</TableHead>
                              <TableHead>Hours</TableHead>
                              <TableHead>Overdue</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {facility.entries.map((shift) => {
                              const scheduledHours = calculateHours(
                                new Date(shift.scheduledStartsAt),
                                new Date(shift.scheduledEndsAt),
                              );
                              const overdue = formatDistanceToNow(
                                new Date(shift.scheduledEndsAt),
                                { addSuffix: false },
                              );

                              return (
                                <TableRow
                                  key={shift.shiftId}
                                  className="hover:bg-slate-50"
                                >
                                  <TableCell>
                                    <div className="font-medium">
                                      {format(
                                        new Date(shift.scheduledStartsAt),
                                        "EEE, MMM d",
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="text-sm text-slate-600">
                                      {format(
                                        new Date(shift.scheduledStartsAt),
                                        "h:mm a",
                                      )}{" "}
                                      -{" "}
                                      {format(
                                        new Date(shift.scheduledEndsAt),
                                        "h:mm a",
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <span className="text-slate-600">
                                      {formatHours(scheduledHours)}
                                    </span>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-1.5 text-sm text-amber-600">
                                      <AlertCircle className="h-4 w-4" />
                                      <span>{overdue}</span>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>

                      {/* Mobile cards */}
                      <div className="sm:hidden space-y-2 p-3">
                        {facility.entries.map((shift) => {
                          const scheduledHours = calculateHours(
                            new Date(shift.scheduledStartsAt),
                            new Date(shift.scheduledEndsAt),
                          );
                          const overdue = formatDistanceToNow(
                            new Date(shift.scheduledEndsAt),
                            { addSuffix: false },
                          );

                          return (
                            <Card key={shift.shiftId} className="p-3">
                              <CardContent className="p-0 space-y-3">
                                <div className="flex items-start justify-between">
                                  <div>
                                    <div className="font-medium">
                                      {format(
                                        new Date(shift.scheduledStartsAt),
                                        "EEEE, MMM d",
                                      )}
                                    </div>
                                    <div className="text-sm text-slate-500">
                                      {format(
                                        new Date(shift.scheduledStartsAt),
                                        "h:mm a",
                                      )}{" "}
                                      -{" "}
                                      {format(
                                        new Date(shift.scheduledEndsAt),
                                        "h:mm a",
                                      )}
                                    </div>
                                  </div>
                                  <Badge
                                    variant="outline"
                                    className="text-xs bg-amber-50 text-amber-700 border-amber-200"
                                  >
                                    {formatHours(scheduledHours)}
                                  </Badge>
                                </div>

                                <div className="flex items-center gap-1.5 text-sm text-amber-600 pt-2 border-t">
                                  <AlertCircle className="h-4 w-4" />
                                  <span>Overdue by {overdue}</span>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </CollapsibleContent>
            </Collapsible>
          </Card>
        );
      })}
    </div>
  );
}
